import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/seo/Seo';
import { breadcrumbSchema } from '../components/seo/jsonld';

const PrivacyPolicyPage = () => (
    <>
        <Seo
            title="Privacy Policy"
            description="How pdfconvertor.shop handles your data. Client-side file processing, Google AdSense cookies, GDPR and CCPA rights, contact details for privacy requests."
            path="/privacy-policy"
            jsonLd={breadcrumbSchema([
                { name: 'Home', path: '/' },
                { name: 'Privacy Policy', path: '/privacy-policy' },
            ])}
        />

        <div className="page-container narrow">
            <article className="legal-page">
                <header className="page-header">
                    <h1>Privacy Policy</h1>
                    <p className="meta">Last updated: April 21, 2026</p>
                </header>

                <p>
                    This Privacy Policy explains how pdfconvertor.shop (&quot;we,&quot;
                    &quot;us,&quot; &quot;the Site&quot;) collects, uses, and discloses
                    information when you visit our website at{' '}
                    <a href="https://pdfconvertor.shop">https://pdfconvertor.shop</a>. It also
                    describes your rights and how to contact us with privacy questions.
                </p>

                <h2>1. Files you convert are never uploaded</h2>
                <p>
                    The core PDF, Word, and image conversion features on this Site run entirely
                    in your web browser using JavaScript and WebAssembly. When you select a file
                    on any tool page, that file is loaded into your browser's memory and
                    processed locally on your device. We do not receive, store, log, transmit,
                    or see your files at any stage of conversion.
                </p>

                <h2>2. Information we do collect</h2>
                <h3>a) Server access logs</h3>
                <p>
                    Our hosting provider may record standard HTTP access logs when you visit the
                    Site. These logs can include:
                </p>
                <ul>
                    <li>Your IP address (which may reveal approximate geographic location)</li>
                    <li>Browser type and version (user agent)</li>
                    <li>Referring URL</li>
                    <li>Pages visited and timestamps</li>
                </ul>
                <p>
                    This information is used solely for operating the Site, diagnosing technical
                    problems, detecting abuse, and producing aggregated traffic statistics. We do
                    not attempt to associate access logs with individual identities.
                </p>

                <h3>b) Local storage</h3>
                <p>
                    We may store small amounts of data in your browser's <code>localStorage</code>
                    {' '}to remember your preferences (for example, whether you have accepted or
                    declined our cookie notice). This data stays on your device and is never
                    transmitted to us.
                </p>

                <h3>c) Analytics</h3>
                <p>
                    We may use privacy-respecting analytics to understand how the Site is used in
                    aggregate (e.g., which tools are most popular). Where analytics cookies are
                    used, they are disclosed in this policy and governed by your consent choice
                    in the cookie banner.
                </p>

                <h2>3. Cookies and similar technologies</h2>
                <p>
                    A <strong>cookie</strong> is a small piece of data stored on your device by
                    your browser. This Site uses cookies for the following purposes:
                </p>
                <ul>
                    <li>
                        <strong>Essential:</strong> to remember your consent choice about cookies.
                    </li>
                    <li>
                        <strong>Advertising:</strong> Google AdSense and its advertising partners
                        may set cookies to personalize and measure the ads you see (see section 4).
                    </li>
                </ul>
                <p>
                    You can block or delete cookies at any time through your browser settings.
                    Blocking essential cookies may cause the consent banner to reappear on every
                    visit.
                </p>

                <h2>4. Google AdSense &amp; third-party advertising</h2>
                <p>
                    This Site is funded by advertisements served through{' '}
                    <strong>Google AdSense</strong>, a service provided by Google LLC. Google, as
                    a third-party vendor, uses cookies to serve ads on the Site. Specifically:
                </p>
                <ul>
                    <li>
                        Google may use the{' '}
                        <strong>DoubleClick DART cookie</strong> to serve ads to our users based on
                        their visit to this Site and other sites on the Internet.
                    </li>
                    <li>
                        Users may opt out of the use of the DART cookie by visiting the{' '}
                        <a
                            href="https://policies.google.com/technologies/ads"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Google ad and content network privacy policy
                        </a>.
                    </li>
                    <li>
                        Users may also manage personalized advertising preferences at{' '}
                        <a
                            href="https://adssettings.google.com"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Google Ads Settings
                        </a>, or opt out of personalized advertising across participating
                        companies at{' '}
                        <a
                            href="https://optout.aboutads.info"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            AboutAds.info
                        </a>.
                    </li>
                </ul>
                <p>
                    Google's advertising technologies are governed by Google's own privacy
                    policy, available at{' '}
                    <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        policies.google.com/privacy
                    </a>. We recommend reviewing the privacy policies of any third-party ad
                    servers for detailed information on their practices.
                </p>
                <p>
                    Some of our advertising partners may use cookies and web beacons on the Site.
                    These third-party ad servers or ad networks automatically receive your IP
                    address when this occurs. These technologies are used to measure the
                    effectiveness of their advertising campaigns or to personalize the
                    advertising content you see. pdfconvertor.shop has no access to, or control
                    over, these cookies that are used by third-party advertisers.
                </p>

                <h2>5. Children&apos;s privacy (COPPA)</h2>
                <p>
                    This Site is not directed at children under the age of 13. We do not
                    knowingly collect personal information from children under 13. If you are a
                    parent or guardian and believe your child has provided personal information
                    to us, please contact us and we will take reasonable steps to delete such
                    information from our records.
                </p>

                <h2>6. Your GDPR rights (European Economic Area)</h2>
                <p>
                    If you are located in the European Economic Area, United Kingdom, or
                    Switzerland, the General Data Protection Regulation (GDPR) provides you with
                    the following rights regarding your personal data:
                </p>
                <ul>
                    <li><strong>Right of access</strong> — request a copy of the personal data we hold about you.</li>
                    <li><strong>Right to rectification</strong> — request correction of inaccurate data.</li>
                    <li><strong>Right to erasure</strong> — request deletion of your personal data.</li>
                    <li><strong>Right to restrict processing</strong> — request that we limit how we use your data.</li>
                    <li><strong>Right to object</strong> — object to processing based on legitimate interests.</li>
                    <li><strong>Right to data portability</strong> — receive your data in a machine-readable format.</li>
                    <li><strong>Right to withdraw consent</strong> — where processing is based on consent.</li>
                    <li><strong>Right to lodge a complaint</strong> with your national data protection authority.</li>
                </ul>

                <h2>7. Your CCPA rights (California)</h2>
                <p>
                    If you are a California resident, the California Consumer Privacy Act (CCPA)
                    provides you with the following rights:
                </p>
                <ul>
                    <li>
                        The right to know what personal information we collect, use, disclose,
                        and sell.
                    </li>
                    <li>
                        The right to request deletion of personal information we have collected
                        from you.
                    </li>
                    <li>
                        The right to opt out of the sale of your personal information. We do not
                        sell personal information as defined under the CCPA.
                    </li>
                    <li>
                        The right not to be discriminated against for exercising your CCPA rights.
                    </li>
                </ul>

                <h2>8. Data retention</h2>
                <p>
                    Because we do not process your files on our servers, we do not retain any
                    file contents at all. Server access logs are retained for a short operational
                    period (typically up to 30 days) and then rotated out. Any data stored in
                    your browser's <code>localStorage</code> remains on your device until you
                    clear it through your browser settings.
                </p>

                <h2>9. Security</h2>
                <p>
                    We serve this Site over HTTPS so that communications between your browser and
                    the server are encrypted in transit. Because conversions happen locally in
                    your browser, there is no server-side copy of your file to secure.
                </p>

                <h2>10. Changes to this policy</h2>
                <p>
                    We may update this Privacy Policy from time to time to reflect changes in
                    our practices, technology, or legal requirements. When we do, we will revise
                    the &quot;Last updated&quot; date at the top of this page. Material changes
                    will be highlighted on the homepage.
                </p>

                <h2>11. Contact us</h2>
                <p>
                    To exercise any of the rights above, or if you have questions about this
                    Privacy Policy, please contact us:
                </p>
                <ul>
                    <li><strong>Email:</strong> <a href="mailto:pdf2convertor@gmail.com">pdf2convertor@gmail.com</a></li>
                    <li><strong>Mailing address:</strong> KPHB 7th Phase, KPHB, Hyderabad, Telangana, India</li>
                    <li><strong>Contact form:</strong> <Link to="/contact">/contact</Link></li>
                </ul>
            </article>
        </div>
    </>
);

export default PrivacyPolicyPage;
